import React, { useState } from 'react';
import { UsageFrequency } from '../../types/place';

interface CreateProductForm {
  name: string;
  description: string;
  originalPrice: string;
  discountedPrice: string;
  discountPercentage: string;
  category: string;
  usageFrequency: UsageFrequency;
  hasTimeRestriction: boolean;
  availableFrom: string;
  availableTo: string;
  imageFile?: File;
  imageUrl?: string;
}

interface CreateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (form: CreateProductForm) => Promise<void>;
  usageFrequencyOptions: { value: UsageFrequency; label: string }[];
  isCreating: boolean;
  error: string;
  setError: (error: string) => void;
}

const CreateProductModal: React.FC<CreateProductModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  usageFrequencyOptions,
  isCreating,
  error,
  setError
}) => {
  const [form, setForm] = useState<CreateProductForm>({
    name: '',
    description: '',
    originalPrice: '',
    discountedPrice: '0,00', // Valor padrão válido
    discountPercentage: '0', // Valor padrão válido
    category: '',
    usageFrequency: UsageFrequency.Unlimited,
    hasTimeRestriction: false,
    availableFrom: '',
    availableTo: '',
  });

  const handleInputChange = (field: keyof CreateProductForm, value: string | boolean) => {
    console.log('CreateProductModal - handleInputChange called with field:', field, 'value:', value);
    console.log('CreateProductModal - Current form state before update:', form);
    
    // Verificação especial para discountPercentage
    if (field === 'discountPercentage') {
      console.log('CreateProductModal - Special handling for discountPercentage field');
      if (typeof value === 'string') {
        const percentage = parseFloat(value);
        if (isNaN(percentage) || percentage < 0 || percentage > 100) {
          console.error('CreateProductModal - Invalid discountPercentage value in handleInputChange:', value);
          setError('Percentual de desconto deve ser entre 0 e 100');
          return;
        }
        console.log('CreateProductModal - Valid discountPercentage value:', percentage);
      }
    }
    
    // Verificação especial para originalPrice
    if (field === 'originalPrice') {
      console.log('CreateProductModal - Special handling for originalPrice field');
      if (typeof value === 'string' && value && form.discountPercentage) {
        const originalPrice = parseFloat(value.replace(',', '.'));
        const discountPercentage = parseFloat(form.discountPercentage);
        
        if (!isNaN(originalPrice) && !isNaN(discountPercentage)) {
          const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
          console.log('CreateProductModal - Auto-calculating discountedPrice:', discountedPrice);
          
          setForm(prev => ({
            ...prev,
            [field]: value,
            discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
          }));
          return;
        }
      }
    }
    
    setForm(prev => {
      const newForm = { ...prev, [field]: value };
      console.log('CreateProductModal - New form state after update:', newForm);
      return newForm;
    });
  };

  const calculateDiscountedPrice = (original: number, percentage: number): number => {
    return original * (1 - percentage / 100);
  };

  const handleOriginalPriceChange = (value: string) => {
    console.log('CreateProductModal - handleOriginalPriceChange called with value:', value);
    console.log('CreateProductModal - Current form discountPercentage:', form.discountPercentage);
    
    // Verificar se o valor é válido
    if (!value || value === '') {
      console.error('CreateProductModal - Empty originalPrice value received');
      setForm(prev => ({ ...prev, originalPrice: value }));
      return;
    }
    
    const originalPrice = parseFloat(value.replace(',', '.'));
    const discountPercentage = parseFloat(form.discountPercentage.replace(',', '.'));
    
    console.log('CreateProductModal - Parsed values in handleOriginalPriceChange:', { originalPrice, discountPercentage });
    
    // Verificar se os valores parseados são válidos
    if (isNaN(originalPrice) || isNaN(discountPercentage)) {
      console.error('CreateProductModal - Invalid parsed values in handleOriginalPriceChange:', { originalPrice, discountPercentage });
      setForm(prev => ({ ...prev, originalPrice: value }));
      return;
    }
    
    if (originalPrice < 0) {
      console.error('CreateProductModal - Original price out of range:', originalPrice);
      setForm(prev => ({ ...prev, originalPrice: value }));
      return;
    }
    
    const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
    console.log('CreateProductModal - Calculated discounted price in handleOriginalPriceChange:', discountedPrice);
    
    setForm(prev => ({
      ...prev,
      originalPrice: value,
      discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
    }));
  };

  const handleDiscountPercentageChange = (value: string) => {
    console.log('CreateProductModal - handleDiscountPercentageChange called with value:', value);
    console.log('CreateProductModal - Current form state:', form);
    
    // Verificar se o valor é válido
    if (!value || value === '') {
      console.error('CreateProductModal - Empty discountPercentage value received');
      return;
    }
    
    const percentage = parseFloat(value.replace(',', '.'));
    const originalPrice = parseFloat(form.originalPrice.replace(',', '.'));
    
    console.log('CreateProductModal - Parsed values:', { percentage, originalPrice });
    
    // Verificar se os valores parseados são válidos
    if (isNaN(percentage) || isNaN(originalPrice)) {
      console.error('CreateProductModal - Invalid parsed values:', { percentage, originalPrice });
      setForm(prev => ({ ...prev, discountPercentage: value }));
      return;
    }
    
    if (percentage < 0 || percentage > 100) {
      console.error('CreateProductModal - Percentage out of range:', percentage);
      setForm(prev => ({ ...prev, discountPercentage: value }));
      return;
    }
    
    const discountedPrice = calculateDiscountedPrice(originalPrice, percentage);
    console.log('CreateProductModal - Calculated discounted price:', discountedPrice);
    
    setForm(prev => {
      const newForm = {
        ...prev,
        discountPercentage: value,
        discountedPrice: discountedPrice.toFixed(2).replace('.', ',')
      };
      console.log('CreateProductModal - New form state after update:', newForm);
      return newForm;
    });
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 5MB');
        return;
      }

      setForm(prev => ({ ...prev, imageFile: file }));
    }
  };

  const removeImage = () => {
    setForm(prev => ({ ...prev, imageFile: undefined, imageUrl: undefined }));
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      originalPrice: '',
      discountedPrice: '0,00', // Valor padrão válido
      discountPercentage: '0', // Valor padrão válido
      category: '',
      usageFrequency: UsageFrequency.Unlimited,
      hasTimeRestriction: false,
      availableFrom: '',
      availableTo: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug: verificar os valores antes do envio
    console.log('CreateProductModal - Form data before submit:', form);
    console.log('CreateProductModal - discountPercentage value:', form.discountPercentage);
    console.log('CreateProductModal - discountPercentage type:', typeof form.discountPercentage);
    console.log('CreateProductModal - discountedPrice value:', form.discountedPrice);
    console.log('CreateProductModal - discountedPrice type:', typeof form.discountedPrice);
    
    // Verificação final antes do envio
    if (!form.discountPercentage || form.discountPercentage === '') {
      console.error('CreateProductModal - discountPercentage is empty before submit');
      setError('Percentual de desconto é obrigatório');
      return;
    }
    
    if (!form.discountedPrice || form.discountedPrice === '') {
      console.error('CreateProductModal - discountedPrice is empty before submit');
      setError('Preço com desconto é obrigatório');
      return;
    }
    
    const discountPercentage = parseFloat(form.discountPercentage);
    const discountedPrice = parseFloat(form.discountedPrice.replace(',', '.'));
    
    if (isNaN(discountPercentage) || discountPercentage < 0 || discountPercentage > 100) {
      console.error('CreateProductModal - Invalid discountPercentage before submit:', form.discountPercentage);
      setError('Percentual de desconto inválido');
      return;
    }
    
    if (isNaN(discountedPrice) || discountedPrice < 0) {
      console.error('CreateProductModal - Invalid discountedPrice before submit:', form.discountedPrice);
      setError('Preço com desconto inválido');
      return;
    }
    
    console.log('CreateProductModal - Validation passed, submitting form with:', {
      discountPercentage,
      discountedPrice
    });
    
    try {
      await onSubmit(form);
      resetForm();
      onClose();
    } catch (err) {
      // Error handling is done in the parent component
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Novo Produto com Desconto</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Produto *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria *
              </label>
              <select
                value={form.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Selecione uma categoria</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div> */}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preço Original (R$) *
              </label>
              <input
                type="text"
                value={form.originalPrice}
                onChange={(e) => handleOriginalPriceChange(e.target.value)}
                placeholder="0,00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Percentual de Desconto (%) *
              </label>
              <input
                type="number"
                value={form.discountPercentage}
                onChange={(e) => handleDiscountPercentageChange(e.target.value)}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preço com Desconto (R$)
              </label>
              <input
                type="text"
                value={form.discountedPrice}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequência de Uso *
              </label>
              <select
                value={form.usageFrequency}
                onChange={(e) => handleInputChange('usageFrequency', e.target.value as UsageFrequency)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {usageFrequencyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Imagem do Produto
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {form.imageFile && (
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{form.imageFile.name}</span>
                  <button
                    type="button"
                    onClick={removeImage}
                    className="text-red-600 hover:text-red-800 text-sm"
                  >
                    Remover
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="hasTimeRestriction"
              checked={form.hasTimeRestriction}
              onChange={(e) => handleInputChange('hasTimeRestriction', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="hasTimeRestriction" className="text-sm font-medium text-gray-700">
              Restrição de Horário
            </label>
          </div> */}

          {form.hasTimeRestriction && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Disponível a partir de
                </label>
                <input
                  type="time"
                  value={form.availableFrom}
                  onChange={(e) => handleInputChange('availableFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Disponível até
                </label>
                <input
                  type="time"
                  value={form.availableTo}
                  onChange={(e) => handleInputChange('availableTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isCreating}
              className="btn btn-primary"
            >
              {isCreating ? 'Criando...' : 'Criar Produto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProductModal;
